import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout, Button } from '../common/index.js';
import DateFilterDropdown from './DateFilterDropdown.js';
import analyticsService from '../../services/AnalyticsService';
import { DollarSign, Users, User, ArrowLeft, AlertCircle, MapPin } from 'lucide-react';

const CommissionAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('current_month');
  const [customDates, setCustomDates] = useState({ from_date: '', to_date: '' });

  useEffect(() => { fetchData(); }, [filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = filter === 'custom' ? { filter, ...customDates } : { filter };
      const res = await analyticsService.getCommissionAnalytics(params);
      setData(res);
    } catch (err) {
      console.error('Error fetching commission analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

  const StatCard = ({ title, total, paid, pending, icon: Icon, color }) => (
    <div className="bg-white rounded-card shadow-card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
        <div className={`p-2 rounded-full ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-800 mb-3">{fmt(total)}</p>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">Paid</span>
          <span className="font-semibold text-green-600">{fmt(paid)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Pending</span>
          <span className="font-semibold text-blue-600">{fmt(pending)}</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-1.5 mt-2">
          <div
            className="bg-green-500 h-1.5 rounded-full"
            style={{ width: total > 0 ? `${Math.min((paid / total) * 100, 100)}%` : '0%' }}
          />
        </div>
      </div>
    </div>
  );

  const StatusBadge = ({ status }) => {
    const map = { PAID: 'bg-green-100 text-green-700', PARTIAL: 'bg-yellow-100 text-yellow-700', PENDING: 'bg-red-100 text-red-700' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${map[status] || map.PENDING}`}>{status}</span>;
  };

  const getHubTypeBadge = (type) => {
    if (type === 'sender') return 'bg-purple-100 text-purple-700';
    if (type === 'receiver') return 'bg-indigo-100 text-indigo-700';
    return 'bg-gray-100 text-gray-700';
  };

  if (loading) return (
    <PageLayout title="Commission Analytics" icon={DollarSign}>
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
          <p className="text-secondary">Loading commission analytics...</p>
        </div>
      </div>
    </PageLayout>
  );

  return (
    <PageLayout
      title="Commission Analytics"
      icon={DollarSign}
      actions={
        <>
          <DateFilterDropdown filter={filter} setFilter={setFilter} customDates={customDates} setCustomDates={setCustomDates} onApply={fetchData} />
          <Button variant="secondary" size="sm" icon={ArrowLeft} onClick={() => navigate('/analytics')}>Back</Button>
        </>
      }
    >
      <p className="text-sm text-secondary -mt-2 mb-6">Company, owner and location hub commission breakdown</p>

                {data && (
                  <>
                    {/* Grand Total Banner */}
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-card shadow-card p-5 mb-6 text-white">
                      <div className="flex flex-wrap gap-6 items-center justify-between">
                        <div>
                          <p className="text-sm opacity-80 mb-1">Grand Total Commission</p>
                          <p className="text-3xl font-bold">{fmt(data.grand_total?.total)}</p>
                        </div>
                        <div className="flex gap-6">
                          <div>
                            <p className="text-xs opacity-70">Paid</p>
                            <p className="text-xl font-bold text-green-300">{fmt(data.grand_total?.paid)}</p>
                          </div>
                          <div>
                            <p className="text-xs opacity-70">Pending</p>
                            <p className="text-xl font-bold text-blue-400">{fmt(data.grand_total?.pending)}</p>
                          </div>
                          <div>
                            <p className="text-xs opacity-70">Total Records</p>
                            <p className="text-xl font-bold">{data.total_commissions}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Three tier cards */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
                      <StatCard title="Primary Agent Commission" total={data.company?.total} paid={data.company?.paid} pending={data.company?.pending} icon={Users} color="bg-green-500" />
                      <StatCard title="Vehicle Owner Commission" total={data.owner?.total} paid={data.owner?.paid} pending={data.owner?.pending} icon={User} color="bg-brand" />
                      <StatCard title="Location Hub Commission" total={data.hubs?.total} paid={data.hubs?.paid} pending={data.hubs?.pending} icon={MapPin} color="bg-purple-500" />
                    </div>

                    {/* Status Breakdown */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
                      {[
                        { label: 'Company Payment Status', breakdown: data.company?.status_breakdown },
                        { label: 'Vehicle Owner Payment Status', breakdown: data.owner?.status_breakdown },
                      ].map(({ label, breakdown }) => (
                        <div key={label} className="bg-white rounded-card shadow-card p-5">
                          <h3 className="text-sm font-semibold text-gray-700 mb-4">{label}</h3>
                          <div className="flex gap-3 flex-wrap">
                            {breakdown && Object.entries(breakdown).map(([status, count]) => (
                              <div key={status} className="flex items-center gap-2 bg-gray-50 rounded-lg px-4 py-3">
                                <StatusBadge status={status} />
                                <span className="text-lg font-bold text-gray-800">{count}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Per Hub Breakdown */}
                    {data.hubs?.per_hub?.length > 0 && (
                      <div className="bg-white rounded-card shadow-card p-5">
                        <h3 className="text-base font-semibold text-gray-800 mb-4">
                          Hub-wise Commission Breakdown ({data.hubs.per_hub.length} hub{data.hubs.per_hub.length !== 1 ? 's' : ''})
                        </h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gray-50 text-left">
                                {['Location', 'Type', 'Hub ID', 'Total Commission', 'Paid', 'Pending', 'Collection %'].map((h) => (
                                  <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {data.hubs.per_hub.map((hub) => {
                                const pct = hub.total > 0 ? ((hub.paid / hub.total) * 100).toFixed(1) : 0;
                                return (
                                  <tr key={hub.hub_id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 font-medium text-gray-800">{hub.location}</td>
                                    <td className="px-4 py-3">
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${getHubTypeBadge(hub.type)}`}>
                                        {hub.type}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-gray-500 text-xs">{hub.hub_id}</td>
                                    <td className="px-4 py-3 font-semibold text-gray-700">{fmt(hub.total)}</td>
                                    <td className="px-4 py-3 text-green-600 font-semibold">{fmt(hub.paid)}</td>
                                    <td className="px-4 py-3 text-blue-600 font-semibold">{fmt(hub.pending)}</td>
                                    <td className="px-4 py-3">
                                      <div className="flex items-center gap-2">
                                        <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                                          <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                                        </div>
                                        <span className="text-xs text-gray-600 w-10">{pct}%</span>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {data.hubs?.per_hub?.length === 0 && (
                      <div className="bg-white rounded-card shadow-card p-8 text-center">
                        <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No hub commissions found for this period.</p>
                      </div>
                    )}
                  </>
                )}
    </PageLayout>
  );
};

export default CommissionAnalytics;
